import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Plus,
  Search,
  MoreVertical,
  FileText,
  Send,
  Check,
  X,
  Download,
  Clock,
  CheckCircle2,
  XCircle,
  Truck,
  Package,
  Trash2,
  Eye,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700", icon: FileText },
  pending_approval: { label: "Pending Approval", color: "bg-amber-50 text-amber-700", icon: Clock },
  approved: { label: "Approved", color: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  sent: { label: "Sent", color: "bg-blue-50 text-blue-700", icon: Send },
  received: { label: "Received", color: "bg-purple-50 text-purple-700", icon: Package },
  cancelled: { label: "Cancelled", color: "bg-red-50 text-red-700", icon: XCircle },
};

const PurchaseOrders = () => {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [formData, setFormData] = useState({
    supplier_id: "",
    items: [],
    notes: "",
    delivery_address: "",
    expected_delivery: "",
    tax_rate: 15,
  });
  const [newItem, setNewItem] = useState({
    inventory_item_id: "",
    quantity: 1,
    unit_price: 0,
  });

  const fetchData = async () => {
    try {
      const [ordersRes, suppliersRes, inventoryRes] = await Promise.all([
        axios.get(`${API}/purchase-orders`, { withCredentials: true }),
        axios.get(`${API}/suppliers`, { withCredentials: true }),
        axios.get(`${API}/inventory`, { withCredentials: true }),
      ]);
      setOrders(ordersRes.data);
      setSuppliers(suppliersRes.data);
      setInventoryItems(inventoryRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load purchase orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddItem = () => {
    if (!newItem.inventory_item_id) {
      toast.error("Please select an item");
      return;
    }

    const inventoryItem = inventoryItems.find(
      (i) => i.item_id === newItem.inventory_item_id
    );
    if (!inventoryItem) return;

    const itemToAdd = {
      inventory_item_id: inventoryItem.item_id,
      item_name: inventoryItem.name,
      quantity: parseFloat(newItem.quantity) || 1,
      unit: inventoryItem.unit || "pcs",
      unit_price: parseFloat(newItem.unit_price) || inventoryItem.unit_cost || 0,
      total_price:
        (parseFloat(newItem.quantity) || 1) *
        (parseFloat(newItem.unit_price) || inventoryItem.unit_cost || 0),
    };

    setFormData({
      ...formData,
      items: [...formData.items, itemToAdd],
    });

    setNewItem({ inventory_item_id: "", quantity: 1, unit_price: 0 });
  };

  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.total_price, 0);
    const taxAmount = subtotal * (formData.tax_rate / 100);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplier_id) {
      toast.error("Please select a supplier");
      return;
    }
    if (formData.items.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    try {
      await axios.post(`${API}/purchase-orders`, formData, { withCredentials: true });
      toast.success("Purchase order created");
      setSheetOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error creating PO:", error);
      toast.error("Failed to create purchase order");
    }
  };

  const ACTION_SUCCESS_MESSAGES = {
    submit: "Order submitted for approval",
    approve: "Order approved",
    reject: "Order rejected",
    send: "Order sent to supplier",
    "mark-received": "Order marked as received",
  };

  const handleAction = async (poId, action) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/purchase-orders/${poId}/${action}`, {}, { withCredentials: true });
      toast.success(ACTION_SUCCESS_MESSAGES[action] || "Action completed");
      fetchData();
      setViewDialogOpen(false);
    } catch (error) {
      console.error(`Error ${action} PO:`, error);
      toast.error(error.response?.data?.detail || `Failed to ${action} order`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPDF = async (poId, poNumber) => {
    try {
      const response = await axios.get(`${API}/purchase-orders/${poId}/pdf`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `PO_${poNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    }
  };

  const handleDelete = async () => {
    if (!orderToDelete) return;
    try {
      await axios.delete(`${API}/purchase-orders/${orderToDelete.po_id}`, {
        withCredentials: true,
      });
      toast.success("Purchase order deleted");
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting PO:", error);
      toast.error(error.response?.data?.detail || "Failed to delete order");
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: "",
      items: [],
      notes: "",
      delivery_address: "",
      expected_delivery: "",
      tax_rate: 15,
    });
    setNewItem({ inventory_item_id: "", quantity: 1, unit_price: 0 });
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.po_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.supplier_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const pendingCount = orders.filter((o) => o.status === "pending_approval").length;

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="purchase-orders-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
            Purchase Orders
          </h1>
          <p className="text-slate-500 mt-1">Create and manage supplier orders</p>
        </div>
        <Button
          className="rounded-sm bg-slate-900 hover:bg-slate-800"
          onClick={() => setSheetOpen(true)}
          data-testid="create-po-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Order
        </Button>
      </div>

      {/* Pending Approval Alert */}
      {pendingCount > 0 && (
        <Card className="rounded-sm border-amber-200 bg-amber-50 mb-6">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="h-5 w-5 text-amber-600" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">
                {pendingCount} order{pendingCount > 1 ? "s" : ""} pending approval
              </p>
              <p className="text-sm text-amber-600">
                Review and approve orders before sending to suppliers
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm border-amber-400 text-amber-700 hover:bg-amber-100"
              onClick={() => setStatusFilter("pending_approval")}
              data-testid="view-pending-btn"
            >
              View Pending
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by PO number or supplier..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="search-po"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48 rounded-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {Object.entries(statusConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>
                {config.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {filteredOrders.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO Number</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => {
                  const status = statusConfig[order.status] || statusConfig.draft;
                  const StatusIcon = status.icon;
                  return (
                    <TableRow key={order.po_id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                            <FileText className="h-5 w-5 text-slate-600" />
                          </div>
                          <span className="font-medium font-mono">
                            {order.po_number}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {order.supplier_name}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {order.items?.length || 0} items
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        R {(order.total_amount || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell>
                        <Badge className={`rounded-sm ${status.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {new Date(order.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedOrder(order);
                                setViewDialogOpen(true);
                              }}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDownloadPDF(order.po_id, order.po_number)}
                            >
                              <Download className="mr-2 h-4 w-4" />
                              Download PDF
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {order.status === "draft" && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleAction(order.po_id, "submit")}
                                >
                                  <Send className="mr-2 h-4 w-4" />
                                  Submit for Approval
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setOrderToDelete(order);
                                    setDeleteDialogOpen(true);
                                  }}
                                  className="text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                            {order.status === "pending_approval" && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleAction(order.po_id, "approve")}
                                >
                                  <Check className="mr-2 h-4 w-4 text-emerald-600" />
                                  Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleAction(order.po_id, "reject")}
                                >
                                  <X className="mr-2 h-4 w-4 text-red-600" />
                                  Reject
                                </DropdownMenuItem>
                              </>
                            )}
                            {order.status === "approved" && (
                              <DropdownMenuItem
                                onClick={() => handleAction(order.po_id, "send")}
                              >
                                <Send className="mr-2 h-4 w-4 text-blue-600" />
                                Send to Supplier
                              </DropdownMenuItem>
                            )}
                            {(order.status === "approved" || order.status === "sent") && (
                              <DropdownMenuItem
                                onClick={() => handleAction(order.po_id, "mark-received")}
                              >
                                <Package className="mr-2 h-4 w-4 text-purple-600" />
                                Mark as Received
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                No purchase orders found
              </h3>
              <p className="text-slate-500 mb-6">
                {searchQuery || statusFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Create your first purchase order"}
              </p>
              {!searchQuery && statusFilter === "all" && (
                <Button
                  onClick={() => setSheetOpen(true)}
                  className="rounded-sm bg-slate-900 hover:bg-slate-800"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Order
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create PO Sheet */}
      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) resetForm();
        }}
      >
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Create Purchase Order</SheetTitle>
            <SheetDescription>
              Create a new order to send to a supplier
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="mt-6 space-y-6">
            {/* Supplier Selection */}
            <div className="space-y-2">
              <Label>Supplier *</Label>
              <Select
                value={formData.supplier_id}
                onValueChange={(value) =>
                  setFormData({ ...formData, supplier_id: value })
                }
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select a supplier" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.supplier_id} value={supplier.supplier_id}>
                      {supplier.name}
                      {supplier.email && (
                        <span className="text-slate-400 ml-2">({supplier.email})</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Add Items */}
            <div className="space-y-4">
              <Label>Order Items *</Label>
              <div className="p-4 bg-slate-50 rounded-sm space-y-3">
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-5">
                    <Select
                      value={newItem.inventory_item_id}
                      onValueChange={(value) => {
                        const item = inventoryItems.find((i) => i.item_id === value);
                        setNewItem({
                          ...newItem,
                          inventory_item_id: value,
                          unit_price: item?.unit_cost || 0,
                        });
                      }}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue placeholder="Select item" />
                      </SelectTrigger>
                      <SelectContent>
                        {inventoryItems.map((item) => (
                          <SelectItem key={item.item_id} value={item.item_id}>
                            {item.name} ({item.current_stock} {item.unit})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="1"
                      placeholder="Qty"
                      value={newItem.quantity}
                      onChange={(e) =>
                        setNewItem({ ...newItem, quantity: e.target.value })
                      }
                      className="rounded-sm"
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Unit Price"
                      value={newItem.unit_price}
                      onChange={(e) =>
                        setNewItem({ ...newItem, unit_price: e.target.value })
                      }
                      className="rounded-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <Button
                      type="button"
                      onClick={handleAddItem}
                      className="w-full rounded-sm bg-slate-900 hover:bg-slate-800"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>

              {/* Items List */}
              {formData.items.length > 0 && (
                <div className="border rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={`form-item-${item.part_id || item.item_name || index}`}>
                          <TableCell>{item.item_name}</TableCell>
                          <TableCell className="text-right">
                            {item.quantity} {item.unit}
                          </TableCell>
                          <TableCell className="text-right">
                            R {item.unit_price.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            R {item.total_price.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600"
                              onClick={() => handleRemoveItem(index)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Totals */}
              {formData.items.length > 0 && (
                <div className="text-right space-y-1">
                  <p className="text-sm text-slate-500">
                    Subtotal: R {calculateTotals().subtotal.toLocaleString()}
                  </p>
                  <p className="text-sm text-slate-500">
                    VAT ({formData.tax_rate}%): R{" "}
                    {calculateTotals().taxAmount.toLocaleString()}
                  </p>
                  <p className="text-lg font-semibold">
                    Total: R {calculateTotals().total.toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            {/* Additional Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expected_delivery">Expected Delivery</Label>
                <Input
                  id="expected_delivery"
                  type="date"
                  value={formData.expected_delivery}
                  onChange={(e) =>
                    setFormData({ ...formData, expected_delivery: e.target.value })
                  }
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tax_rate">Tax Rate (%)</Label>
                <Input
                  id="tax_rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.tax_rate}
                  onChange={(e) =>
                    setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })
                  }
                  className="rounded-sm"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="delivery_address">Delivery Address</Label>
              <Textarea
                id="delivery_address"
                value={formData.delivery_address}
                onChange={(e) =>
                  setFormData({ ...formData, delivery_address: e.target.value })
                }
                placeholder="Enter delivery address"
                rows={2}
                className="rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes for the supplier"
                rows={2}
                className="rounded-sm"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSheetOpen(false);
                  resetForm();
                }}
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800"
                data-testid="save-po-btn"
              >
                Create Order
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* View Order Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Purchase Order - {selectedOrder?.po_number}</DialogTitle>
            <DialogDescription>
              Created on{" "}
              {selectedOrder && new Date(selectedOrder.created_at).toLocaleDateString()}
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Status:</span>
                <Badge
                  className={`rounded-sm ${
                    statusConfig[selectedOrder.status]?.color || statusConfig.draft.color
                  }`}
                >
                  {statusConfig[selectedOrder.status]?.label || "Draft"}
                </Badge>
              </div>

              {/* Supplier Info */}
              <div className="p-4 bg-slate-50 rounded-sm">
                <p className="text-sm text-slate-500 mb-1">Supplier</p>
                <p className="font-medium">{selectedOrder.supplier_name}</p>
                {selectedOrder.supplier_email && (
                  <p className="text-sm text-slate-600">{selectedOrder.supplier_email}</p>
                )}
              </div>

              {/* Items */}
              <div>
                <p className="text-sm font-medium mb-2">Items</p>
                <div className="border rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedOrder.items?.map((item, index) => (
                        <TableRow key={`order-item-${item.part_id || item.item_name || index}`}>
                          <TableCell>{item.item_name}</TableCell>
                          <TableCell className="text-right">
                            {item.quantity} {item.unit}
                          </TableCell>
                          <TableCell className="text-right">
                            R {item.unit_price?.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            R {item.total_price?.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Totals */}
              <div className="text-right space-y-1 p-4 bg-slate-50 rounded-sm">
                <p className="text-sm text-slate-500">
                  Subtotal: R {selectedOrder.subtotal?.toLocaleString()}
                </p>
                <p className="text-sm text-slate-500">
                  VAT ({selectedOrder.tax_rate}%): R{" "}
                  {selectedOrder.tax_amount?.toLocaleString()}
                </p>
                <p className="text-lg font-semibold">
                  Total: R{" "}
                  {selectedOrder.total_amount?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </p>
              </div>

              {/* Notes */}
              {selectedOrder.notes && (
                <div>
                  <p className="text-sm font-medium mb-1">Notes</p>
                  <p className="text-sm text-slate-600">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Delivery */}
              {selectedOrder.delivery_address && (
                <div>
                  <p className="text-sm font-medium mb-1">Delivery Address</p>
                  <p className="text-sm text-slate-600">{selectedOrder.delivery_address}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => handleDownloadPDF(selectedOrder?.po_id, selectedOrder?.po_number)}
              className="rounded-sm"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            {selectedOrder?.status === "draft" && (
              <Button
                onClick={() => handleAction(selectedOrder.po_id, "submit")}
                disabled={actionLoading}
                className="rounded-sm bg-slate-900 hover:bg-slate-800"
              >
                {actionLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Submit for Approval
              </Button>
            )}
            {selectedOrder?.status === "pending_approval" && (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleAction(selectedOrder.po_id, "reject")}
                  disabled={actionLoading}
                  className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"
                >
                  <X className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={() => handleAction(selectedOrder.po_id, "approve")}
                  disabled={actionLoading}
                  className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
                >
                  {actionLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Approve
                </Button>
              </>
            )}
            {selectedOrder?.status === "approved" && (
              <Button
                onClick={() => handleAction(selectedOrder.po_id, "send")}
                disabled={actionLoading}
                className="rounded-sm bg-blue-600 hover:bg-blue-700"
              >
                {actionLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send to Supplier
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Purchase Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete order "{orderToDelete?.po_number}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PurchaseOrders;
